import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Globe, Menu, X, ChevronRight, ArrowRight, CheckCircle, 
  Settings, ShoppingCart, MessageSquare, Phone, Mail, 
  MapPin, Factory, ShieldCheck, ChevronDown, Search,
  Image as ImageIcon, AlertCircle, Check, Lock, LogOut,
  FileText, Users, Trash2, Edit, Plus, Save, Eye, Key
} from 'lucide-react';

// --- Firebase Imports ---
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken,
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot 
} from 'firebase/firestore';

// --- Firebase Configuration & Initialization (CRITICAL FIX) ---

// 1. 已根据您提供的截图填入真实配置
const MANUAL_FIREBASE_CONFIG = {
  apiKey: "AIzaSyBqdkksBBIpQYE5-oHfyybFHMOWHQjn5Ok",
  authDomain: "llvalve-website.firebaseapp.com",
  projectId: "llvalve-website",
  storageBucket: "llvalve-website.firebasestorage.app",
  messagingSenderId: "234702950352",
  appId: "1:234702950352:web:a1d226eca7cbfdde5ff3d5",
  measurementId: "G-J02LDFZS8N"
};

// 2. 智能初始化逻辑：自动判断是预览环境还是生产环境
let firebaseConfig;
let appId = 'llvalve-website'; // 默认使用您的项目ID

try {
  // 尝试检测预览环境注入的变量
  if (typeof __firebase_config !== 'undefined') {
    firebaseConfig = JSON.parse(__firebase_config);
    appId = typeof __app_id !== 'undefined' ? __app_id : 'llvalve-website';
  } else {
    // 如果没有注入变量（如 Cloudflare 环境），使用手动配置
    firebaseConfig = MANUAL_FIREBASE_CONFIG;
  }
} catch (e) {
  console.warn("Using manual config due to environment check.");
  firebaseConfig = MANUAL_FIREBASE_CONFIG;
}

// 3. 安全初始化 Firebase
let app, auth, db;
let firebaseInitialized = false;

try {
  // 只有当配置中有 apiKey 时才初始化
  if (firebaseConfig && firebaseConfig.apiKey) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
    firebaseInitialized = true;
  } else {
    console.warn("Firebase config is missing.");
  }
} catch (error) {
  console.error("Firebase Initialization Error:", error);
}

// --- Default Data & Configuration ---

const THEME = {
  primary: 'bg-blue-950', 
  primaryText: 'text-blue-950',
  secondary: 'bg-slate-600',
  accent: 'bg-orange-600', 
  accentHover: 'hover:bg-orange-700',
  accentText: 'text-orange-600',
  lightBg: 'bg-slate-50',
};

// Default Images (Fallback)
const DEFAULT_IMAGES = {
  hero: "https://images.unsplash.com/photo-1516937941348-c09e554b98e5?auto=format&fit=crop&q=80&w=2000", 
  about: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&q=80&w=1200", 
};

// Static Products Data
const PRODUCTS = [
  { id: 1, name: { en: 'High Pressure Ball Valve', zh: '高压固定球阀' }, cat: 'Ball Valves', catZh: '球阀', img: "https://images.unsplash.com/photo-1535068228303-c393848b6186?auto=format&fit=crop&q=80&w=800", specs: 'API 6D / Class 150-2500' },
  { id: 2, name: { en: 'Industrial Gate Valve', zh: '工业楔式闸阀' }, cat: 'Gate Valves', catZh: '闸阀', img: "https://images.unsplash.com/photo-1565514020125-635e6962383a?auto=format&fit=crop&q=80&w=800", specs: 'API 600 / Cast Steel' },
  { id: 3, name: { en: 'Triple Offset Butterfly', zh: '三偏心蝶阀' }, cat: 'Butterfly Valves', catZh: '蝶阀', img: "https://images.unsplash.com/photo-1542013936-64019a88b51e?auto=format&fit=crop&q=80&w=800", specs: 'Zero Leakage / Metal Seat' },
  { id: 4, name: { en: 'Precision Control Valve', zh: '精密气动调节阀' }, cat: 'Control Valves', catZh: '调节阀', img: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&q=80&w=800", specs: '4-20mA / HART Protocol' },
  { id: 5, name: { en: 'Cryogenic Globe Valve', zh: '低温截止阀' }, cat: 'Control Valves', catZh: '调节阀', img: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&q=80&w=800", specs: '-196°C / BS 6364' },
  { id: 6, name: { en: 'Wafer Check Valve', zh: '对夹式止回阀' }, cat: 'Butterfly Valves', catZh: '蝶阀', img: "https://images.unsplash.com/photo-1542013936-64019a88b51e?auto=format&fit=crop&q=80&w=800", specs: 'API 594 / Stainless Steel' },
];

const TRANSLATIONS = {
  en: {
    nav: { home: 'Home', products: 'Products', custom: 'Custom Solutions', about: 'About Us', contact: 'Contact' },
    hero: {
      slogan: 'Precision Valve Solutions for Global Industries',
      sub: 'Engineered for reliability. Certified for safety. Delivering excellence since 2004.',
      cta1: 'Product Catalog',
      cta2: 'Get Consultation'
    },
    services: {
      consult: { title: 'Technical Consultation', desc: 'Expert engineering support for complex fluid control challenges.' },
      order: { title: 'Product Ordering', desc: 'Streamlined procurement for standard and specialized valves.' },
      custom: { title: 'Custom Manufacturing', desc: 'Tailored solutions for extreme temperatures and pressures.' }
    },
    stats: { exp: 'Years Experience', cases: 'Success Cases', countries: 'Countries Served', iso: 'ISO Certified' },
    products: {
      title: 'Product Series',
      viewAll: 'View All Products',
      cats: ['All', 'Ball Valves', 'Gate Valves', 'Butterfly Valves', 'Control Valves'],
      inquire: 'Inquire Now'
    },
    news: {
      title: 'Latest News',
      readMore: 'Read More',
      noNews: 'No news updates at the moment.'
    },
    customPage: {
      title: 'Customization Process',
      steps: ['Requirement', 'Design', 'Prototype', 'Production', 'Delivery']
    },
    contact: {
      title: 'Get a Quote',
      name: 'Name',
      email: 'Email',
      message: 'Project Details',
      submit: 'Send Inquiry',
      sent: 'Message Sent Successfully!'
    }
  },
  zh: {
    nav: { home: '首页', products: '产品中心', custom: '定制服务', about: '关于我们', contact: '联系我们' },
    hero: {
      slogan: '为全球工业提供精密阀门解决方案',
      sub: '专注于可靠性工程。国际安全认证。始于2004年的卓越品质。',
      cta1: '浏览产品目录',
      cta2: '获取专家咨询'
    },
    services: {
      consult: { title: '业务咨询', desc: '针对复杂流体控制挑战的专家级工程支持。' },
      order: { title: '产品订购', desc: '标准及特种阀门的高效采购流程。' },
      custom: { title: '定制生产', desc: '针对极端温度和压力环境的定制化解决方案。' }
    },
    stats: { exp: '年行业经验', cases: '成功案例', countries: '国家服务覆盖', iso: '国际质量认证' },
    products: {
      title: '核心产品系列',
      viewAll: '查看所有产品',
      cats: ['全部', '球阀', '闸阀', '蝶阀', '调节阀'],
      inquire: '立即询价'
    },
    news: {
      title: '企业动态',
      readMore: '阅读更多',
      noNews: '暂无最新动态'
    },
    customPage: {
      title: '定制化生产流程',
      steps: ['需求沟通', '方案设计', '样品确认', '批量生产', '质检交付']
    },
    contact: {
      title: '获取报价',
      name: '姓名',
      email: '电子邮箱',
      message: '项目详情',
      submit: '发送询盘',
      sent: '发送成功！我们会尽快联系您。'
    }
  }
};

// --- Helper Components ---

const SafeImage = ({ src, alt, className }) => {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { setError(false); setLoaded(false); }, [src]);

  if (error || !src) {
    return (
      <div className={`flex flex-col items-center justify-center bg-slate-100 text-slate-400 ${className} border-b border-slate-100`}>
        <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center mb-2">
            <ImageIcon size={20} strokeWidth={1.5} className="opacity-50" />
        </div>
        <span className="text-[10px] uppercase font-bold tracking-wider opacity-60">No Image</span>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {!loaded && <div className="absolute inset-0 bg-slate-100 animate-pulse z-10" />}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
};

const Button = ({ children, variant = 'primary', className = '', onClick, disabled, type="button" }) => {
  const baseStyle = "px-6 py-3 rounded-md font-medium transition-all duration-300 flex items-center justify-center gap-2 relative overflow-hidden active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: `${THEME.accent} hover:bg-orange-700 text-white shadow-lg shadow-orange-600/20 border border-transparent`,
    outline: `border-2 border-white text-white hover:bg-white hover:text-blue-900 backdrop-blur-sm bg-white/5`,
    secondary: `bg-slate-100 text-slate-800 hover:bg-slate-200`,
    ghost: `text-slate-600 hover:text-orange-600`,
    danger: `bg-red-500 hover:bg-red-600 text-white`
  };
  
  return (
    <button 
      type={type}
      className={`${baseStyle} ${variants[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

const SectionHeading = ({ title, subtitle, light = false }) => (
  <div className="mb-12 text-center">
    <motion.h2 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`text-3xl md:text-4xl font-bold mb-4 ${light ? 'text-white' : 'text-slate-900'}`}
    >
      {title}
    </motion.h2>
    {subtitle && (
      <motion.div 
        initial={{ opacity: 0, width: 0 }}
        whileInView={{ opacity: 1, width: 80 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className={`h-1 mx-auto ${THEME.accent}`} 
      />
    )}
  </div>
);

// --- Admin Components ---

const AdminLogin = ({ onLogin }) => {
  const [pass, setPass] = useState('');
  const [err, setErr] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    if (pass === 'admin123') {
      onLogin(true);
    } else {
      setErr('Invalid Password');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="bg-white p-8 rounded-xl shadow-xl w-full max-w-md">
        <h2 className="text-2xl font-bold mb-6 text-slate-800 flex items-center gap-2">
          <Lock className="text-orange-600" /> Admin Access
        </h2>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Password</label>
            <input 
              type="password" 
              className="w-full border p-3 rounded focus:ring-2 focus:ring-orange-500 outline-none"
              value={pass}
              onChange={e => setPass(e.target.value)}
              placeholder="Enter admin password"
            />
          </div>
          {err && <p className="text-red-500 text-sm">{err}</p>}
          <Button type="submit" className="w-full">Login</Button>
        </form>
      </div>
    </div>
  );
};

const AdminDashboard = ({ inquiries, news, imagesConfig, user, onLogout, firebaseReady }) => {
  const [activeTab, setActiveTab] = useState('inquiries');
  const [newNews, setNewNews] = useState({ title: '', date: '', content: '' });
  const [imgForm, setImgForm] = useState({ key: '', url: '' });

  // Handlers for Firestore
  const handleDeleteInquiry = async (id) => {
    if (!firebaseReady || !user) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'inquiries', id));
    } catch (e) { console.error(e); }
  };

  const handleAddNews = async (e) => {
    e.preventDefault();
    if (!firebaseReady || !user) return;
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'news'), {
        ...newNews,
        timestamp: Date.now()
      });
      setNewNews({ title: '', date: '', content: '' });
    } catch (e) { console.error(e); }
  };

  const handleDeleteNews = async (id) => {
    if (!firebaseReady || !user) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'news', id));
    } catch (e) { console.error(e); }
  };

  const handleSaveImage = async (e) => {
    e.preventDefault();
    if (!firebaseReady || !user || !imgForm.key) return;
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'images'), {
        key: imgForm.key,
        url: imgForm.url,
        timestamp: Date.now()
      });
      setImgForm({ key: '', url: '' });
      alert('Image updated! Refresh may be needed depending on sorting.');
    } catch (e) { console.error(e); }
  };

  if (!firebaseReady) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 flex-col gap-4 p-8 text-center">
            <AlertCircle size={48} className="text-red-500" />
            <h2 className="text-xl font-bold">Firebase Configuration Missing</h2>
            <p className="max-w-md text-slate-600">
                Please check console logs for Firebase initialization errors.
            </p>
            <Button onClick={onLogout}>Back to Home</Button>
        </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="bg-blue-950 text-white p-4 flex justify-between items-center shadow-md">
        <h1 className="text-xl font-bold flex items-center gap-2"><Settings /> Console / LLValve Admin</h1>
        <button onClick={onLogout} className="flex items-center gap-2 text-sm hover:text-orange-400"><LogOut size={16}/> Logout</button>
      </div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-slate-200 p-4 space-y-2">
          <button onClick={() => setActiveTab('inquiries')} className={`w-full text-left p-3 rounded flex items-center gap-2 ${activeTab === 'inquiries' ? 'bg-orange-50 text-orange-600 font-bold' : 'text-slate-600 hover:bg-slate-50'}`}>
            <MessageSquare size={18} /> Inquiries <span className="ml-auto bg-slate-200 text-xs px-2 py-0.5 rounded-full">{inquiries.length}</span>
          </button>
          <button onClick={() => setActiveTab('news')} className={`w-full text-left p-3 rounded flex items-center gap-2 ${activeTab === 'news' ? 'bg-orange-50 text-orange-600 font-bold' : 'text-slate-600 hover:bg-slate-50'}`}>
            <FileText size={18} /> News Management
          </button>
          <button onClick={() => setActiveTab('images')} className={`w-full text-left p-3 rounded flex items-center gap-2 ${activeTab === 'images' ? 'bg-orange-50 text-orange-600 font-bold' : 'text-slate-600 hover:bg-slate-50'}`}>
            <ImageIcon size={18} /> Site Images
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-8 overflow-y-auto">
          {activeTab === 'inquiries' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-slate-800">Customer Inquiries</h2>
              <div className="space-y-4">
                {inquiries.length === 0 ? <p className="text-slate-500">No inquiries yet.</p> : inquiries.map(inq => (
                  <div key={inq.id} className="bg-white p-6 rounded-lg shadow-sm border border-slate-100 relative group">
                    <button onClick={() => handleDeleteInquiry(inq.id)} className="absolute top-4 right-4 text-slate-300 hover:text-red-500"><Trash2 size={18} /></button>
                    <div className="flex gap-4 mb-2">
                      <span className="font-bold text-slate-800">{inq.name}</span>
                      <span className="text-slate-500 text-sm bg-slate-100 px-2 rounded">{inq.email}</span>
                      <span className="text-slate-400 text-sm ml-auto">{new Date(inq.timestamp).toLocaleDateString()}</span>
                    </div>
                    <p className="text-slate-600 mt-2 bg-slate-50 p-3 rounded">{inq.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'news' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-slate-800">News Management</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                  <form onSubmit={handleAddNews} className="bg-white p-6 rounded-lg shadow-sm border border-slate-100 sticky top-4">
                    <h3 className="font-bold mb-4 flex items-center gap-2"><Plus size={18}/> Add News</h3>
                    <div className="space-y-4">
                      <input required type="text" placeholder="Title" className="w-full border p-2 rounded" value={newNews.title} onChange={e => setNewNews({...newNews, title: e.target.value})} />
                      <input required type="date" className="w-full border p-2 rounded" value={newNews.date} onChange={e => setNewNews({...newNews, date: e.target.value})} />
                      <textarea required placeholder="Content" rows="4" className="w-full border p-2 rounded" value={newNews.content} onChange={e => setNewNews({...newNews, content: e.target.value})}></textarea>
                      <Button type="submit" className="w-full">Publish News</Button>
                    </div>
                  </form>
                </div>
                <div className="lg:col-span-2 space-y-4">
                  {news.map(n => (
                    <div key={n.id} className="bg-white p-6 rounded-lg shadow-sm flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-lg">{n.title}</h4>
                        <p className="text-xs text-slate-400 mb-2">{n.date}</p>
                        <p className="text-slate-600 text-sm line-clamp-2">{n.content}</p>
                      </div>
                      <button onClick={() => handleDeleteNews(n.id)} className="text-red-400 hover:text-red-600 p-2"><Trash2 size={18} /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'images' && (
            <div>
              <h2 className="text-2xl font-bold mb-6 text-slate-800">Site Configuration</h2>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-100 max-w-2xl mb-8">
                <h3 className="font-bold mb-4 flex items-center gap-2"><Edit size={18}/> Update Site Images</h3>
                <form onSubmit={handleSaveImage} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Image Key (e.g., 'hero', 'about')</label>
                    <select className="w-full border p-2 rounded" value={imgForm.key} onChange={e => setImgForm({...imgForm, key: e.target.value})}>
                      <option value="">Select placement...</option>
                      <option value="hero">Homepage Hero Banner</option>
                      <option value="about">About Us Image</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">New Image URL</label>
                    <input type="url" required placeholder="https://..." className="w-full border p-2 rounded" value={imgForm.url} onChange={e => setImgForm({...imgForm, url: e.target.value})} />
                  </div>
                  <Button type="submit">Update Image</Button>
                </form>
              </div>
              
              <h3 className="font-bold mb-4">Current Configurations</h3>
              <div className="grid grid-cols-2 gap-4">
                 <div className="bg-white p-4 rounded border">
                    <p className="font-mono text-xs text-slate-500 mb-2">Key: hero</p>
                    <SafeImage src={imagesConfig['hero'] || DEFAULT_IMAGES.hero} className="w-full h-32 rounded object-cover" />
                 </div>
                 <div className="bg-white p-4 rounded border">
                    <p className="font-mono text-xs text-slate-500 mb-2">Key: about</p>
                    <SafeImage src={imagesConfig['about'] || DEFAULT_IMAGES.about} className="w-full h-32 rounded object-cover" />
                 </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Pages / Sections ---

const Hero = ({ lang, setPage, dynamicImage }) => {
  const t = TRANSLATIONS[lang].hero;
  return (
    <section className="relative min-h-[750px] lg:h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <SafeImage src={dynamicImage || DEFAULT_IMAGES.hero} alt="Industrial Valve Factory" className="w-full h-full" />
        <div className="absolute inset-0 bg-blue-950/80 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/50 to-transparent" />
      </div>

      <div className="container mx-auto px-6 relative z-10 pt-40 md:pt-48 pb-32 md:pb-52">
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-8 border border-orange-500/30 rounded bg-orange-500/10 backdrop-blur-md">
             <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></span>
             <span className="text-orange-300 text-[10px] font-bold tracking-[0.2em] uppercase">LLVALVE.COM</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 drop-shadow-2xl">
            {t.slogan}
          </h1>
          <p className="text-lg md:text-xl text-slate-200 mb-10 max-w-2xl font-light leading-relaxed drop-shadow-md">
            {t.sub}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button onClick={() => setPage('products')}>
              {t.cta1} <ArrowRight size={18} />
            </Button>
            <Button variant="outline" onClick={() => setPage('contact')}>
              {t.cta2}
            </Button>
          </div>
        </motion.div>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white/5 to-transparent z-10 pointer-events-none" />
    </section>
  );
};

const ServicesGrid = ({ lang, setPage }) => {
  const t = TRANSLATIONS[lang].services;
  const services = [
    { key: 'consult', icon: <MessageSquare size={32} />, color: 'bg-blue-50', link: 'contact' },
    { key: 'order', icon: <ShoppingCart size={32} />, color: 'bg-orange-50', link: 'products' },
    { key: 'custom', icon: <Settings size={32} />, color: 'bg-slate-50', link: 'custom' },
  ];

  return (
    <section className="relative z-20 -mt-32 pb-20 px-4">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((svc, index) => (
            <motion.div
              key={svc.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ translateY: -8 }}
              className={`${svc.color} p-8 rounded-xl border border-white/80 shadow-xl cursor-pointer group backdrop-blur-md bg-opacity-95 flex flex-col items-start`}
              onClick={() => setPage(svc.link)}
            >
              <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-6 bg-white shadow-sm text-slate-700 group-hover:${THEME.primaryText} transition-colors ring-1 ring-slate-100`}>
                {svc.icon}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{t[svc.key].title}</h3>
              <p className="text-slate-600 mb-6 leading-relaxed text-sm flex-grow">{t[svc.key].desc}</p>
              <div className={`flex items-center text-sm font-bold ${THEME.accentText} uppercase tracking-wider mt-auto`}>
                Read More <ChevronRight size={16} className="ml-1 transition-transform group-hover:translate-x-1" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const NewsSection = ({ lang, news }) => {
  const t = TRANSLATIONS[lang].news;
  // Sort news by date (latest first) in memory
  const sortedNews = [...news].sort((a, b) => b.timestamp - a.timestamp).slice(0, 3);

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <SectionHeading title={t.title} subtitle />
        
        {sortedNews.length === 0 ? (
          <div className="text-center text-slate-400 py-10">{t.noNews}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sortedNews.map((item, idx) => (
              <motion.div 
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-slate-50 rounded-xl overflow-hidden group hover:shadow-lg transition-all"
              >
                <div className="p-8">
                  <div className="text-sm text-orange-600 font-bold mb-2">{item.date}</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4 line-clamp-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm line-clamp-3 mb-6">{item.content}</p>
                  <button className="text-blue-900 font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t.readMore} <ArrowRight size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const StatsSection = ({ lang }) => {
  const t = TRANSLATIONS[lang].stats;
  const stats = [
    { val: '20+', label: t.exp, icon: <Factory /> },
    { val: '500+', label: t.cases, icon: <ShieldCheck /> },
    { val: '50+', label: t.countries, icon: <Globe /> },
    { val: 'ISO9001', label: t.iso, icon: <CheckCircle /> },
  ];

  return (
    <section className="py-20 bg-slate-950 text-white border-y border-slate-900 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-8">
          {stats.map((stat, idx) => (
            <div key={idx} className="text-center md:border-r border-slate-800 last:border-0 p-4">
              <div className="flex justify-center mb-4 text-orange-500 opacity-90 scale-110">{stat.icon}</div>
              <div className="text-3xl md:text-5xl font-bold mb-2 text-white tracking-tight">{stat.val}</div>
              <div className="text-xs md:text-sm text-slate-400 uppercase tracking-widest font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProductShowcase = ({ lang, limit = false, setPage }) => {
  const t = TRANSLATIONS[lang].products;
  const [activeCat, setActiveCat] = useState(t.cats[0]);
  
  let displayProducts = PRODUCTS;
  if (!limit && activeCat !== t.cats[0]) {
      displayProducts = PRODUCTS.filter(p => lang === 'en' ? p.cat === activeCat : p.catZh === activeCat);
  }
  if (limit) {
      displayProducts = PRODUCTS.slice(0, 3);
  }

  return (
    <section className="py-24 bg-slate-50">
      <div className="container mx-auto px-6">
        <SectionHeading title={t.title} subtitle />
        
        {!limit && (
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {t.cats.map((cat, idx) => (
              <button 
                key={idx} 
                onClick={() => setActiveCat(cat)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                    activeCat === cat 
                    ? 'bg-blue-950 text-white shadow-lg shadow-blue-900/20 scale-105' 
                    : 'bg-white text-slate-600 hover:bg-slate-200 border border-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        <div className={`grid grid-cols-1 sm:grid-cols-2 ${limit ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-8`}>
          <AnimatePresence mode='popLayout'>
          {displayProducts.map((product) => (
            <motion.div 
              key={product.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group border border-slate-100 flex flex-col h-full"
            >
              <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden shrink-0">
                <div className="absolute top-3 left-3 z-10 bg-blue-950 text-white text-[10px] font-bold px-2 py-1 rounded tracking-wider shadow-sm">
                  PREMIUM
                </div>
                <SafeImage 
                  src={product.img} 
                  alt={product.name[lang]} 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              
              <div className="p-6 flex flex-col flex-grow">
                <div className="text-xs font-bold text-orange-600 mb-2 uppercase tracking-wide">
                  {product.cat}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 leading-tight min-h-[3rem]">{product.name[lang]}</h3>
                <p className="text-xs text-slate-500 mb-5 border-t border-slate-100 pt-3 font-mono">
                  {product.specs}
                </p>
                <button 
                  onClick={() => setPage('contact')}
                  className="mt-auto w-full py-2.5 rounded bg-slate-50 text-slate-700 font-bold text-sm hover:bg-blue-950 hover:text-white transition-colors border border-slate-200 hover:border-blue-950"
                >
                  {t.inquire}
                </button>
              </div>
            </motion.div>
          ))}
          </AnimatePresence>
        </div>
        
        {limit && (
           <div className="mt-12 text-center">
             <Button variant="secondary" onClick={() => setPage('products')} className="mx-auto border border-slate-200">
               {t.viewAll}
             </Button>
           </div>
        )}
      </div>
    </section>
  );
};

const CustomProcess = ({ lang, setPage }) => {
  const t = TRANSLATIONS[lang].customPage;
  
  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <SectionHeading title={t.title} subtitle />
        
        <div className="relative mt-20 mb-20">
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-slate-100 -translate-y-1/2 z-0 rounded-full" />
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 relative z-10">
            {t.steps.map((step, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="flex flex-col items-center text-center group"
              >
                <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center text-xl font-bold mb-6 shadow-xl transition-all duration-300 ${idx % 2 === 0 ? 'bg-blue-950 text-white scale-110' : 'bg-white text-blue-950 border-4 border-blue-50 group-hover:border-orange-200'}`}>
                  {idx + 1}
                </div>
                <h4 className="text-lg font-bold text-slate-800 mb-2">{step}</h4>
                <div className="text-slate-400 text-xs font-mono hidden md:block uppercase tracking-widest">
                    Step {idx + 1}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-950 to-slate-900 p-8 md:p-12 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-12 opacity-5 transform translate-x-10 -translate-y-10">
             <Settings size={200} text="white" />
          </div>
          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-white mb-3">Need Special Specifications?</h3>
            <p className="text-blue-200 max-w-xl">We specialize in Alloy Steel, High Temp (800°C), and Cryogenic customization with full material traceability.</p>
          </div>
          <Button variant="primary" className="relative z-10 shrink-0" onClick={() => setPage('contact')}>Start Customization</Button>
        </div>
      </div>
    </section>
  );
};

const ContactForm = ({ lang, firebaseReady, user }) => {
  const t = TRANSLATIONS[lang].contact;
  const [isSent, setIsSent] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firebaseReady) {
      alert("Please configure Firebase first!");
      return;
    }
    
    // Auth check logic would normally go here, simplified for manual config use case
    // if (!user) return; 

    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'inquiries'), {
        ...formData,
        timestamp: Date.now()
      });
      setIsSent(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setIsSent(false), 3000);
    } catch (e) {
      console.error("Error submitting form", e);
      // Fallback UI behavior even if DB fails due to rules
      setIsSent(true); 
      setTimeout(() => setIsSent(false), 3000);
    }
  };

  return (
    <section className="py-24 bg-slate-50 border-t border-slate-200">
      <div className="container mx-auto px-6">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row">
          
          <div className="bg-blue-950 p-10 md:p-12 text-white md:w-2/5 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-orange-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
            
            <div className="relative z-10">
              <h3 className="text-2xl font-bold mb-8">{lang === 'en' ? 'Contact Info' : '联系方式'}</h3>
              <div className="space-y-8">
                <div className="flex items-start gap-5 group">
                  <div className="w-10 h-10 rounded bg-blue-900 flex items-center justify-center shrink-0 group-hover:bg-orange-600 transition-colors">
                    <MapPin size={20} className="text-white" />
                  </div>
                  <div>
                    <h5 className="font-bold text-sm mb-1 text-blue-200">Headquarters</h5>
                    <p className="text-sm text-slate-300 leading-relaxed">No. 88 Industrial Park, Pudong District, Shanghai, China</p>
                  </div>
                </div>
                 <div className="flex items-start gap-5 group">
                   <div className="w-10 h-10 rounded bg-blue-900 flex items-center justify-center shrink-0 group-hover:bg-orange-600 transition-colors">
                    <Phone size={20} className="text-white" />
                  </div>
                  <div>
                    <h5 className="font-bold text-sm mb-1 text-blue-200">Phone</h5>
                    <p className="text-sm text-slate-300">+86-21-5555-8888</p>
                  </div>
                </div>
                <div className="flex items-start gap-5 group">
                   <div className="w-10 h-10 rounded bg-blue-900 flex items-center justify-center shrink-0 group-hover:bg-orange-600 transition-colors">
                    <Mail size={20} className="text-white" />
                  </div>
                  <div>
                    <h5 className="font-bold text-sm mb-1 text-blue-200">Email</h5>
                    <p className="text-sm text-slate-300">sales@llvalve.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-10 md:p-12 md:w-3/5">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">{t.title}</h3>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t.name}</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 rounded bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t.email}</label>
                  <input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 rounded bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-all" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Subject</label>
                <div className="relative">
                    <input type="text" className="w-full px-4 py-3 pl-10 rounded bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-all" placeholder="e.g. Ball Valve Q41F Quote" />
                    <Search className="absolute left-3 top-3.5 text-slate-400" size={18} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t.message}</label>
                <textarea rows="4" value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} className="w-full px-4 py-3 rounded bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-all resize-none"></textarea>
              </div>
              <Button type="submit" className={`w-full ${isSent ? 'bg-green-600 hover:bg-green-700' : ''}`} disabled={isSent}>
                {isSent ? <><Check size={18} /> {t.sent}</> : t.submit}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ lang, setPage }) => (
  <footer className="bg-slate-950 text-slate-300 pt-20 pb-10 border-t border-slate-900">
    <div className="container mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
        <div className="col-span-1 md:col-span-1">
          <div className="text-2xl font-extrabold text-white mb-6 tracking-tighter flex items-center gap-2 cursor-pointer" onClick={() => setPage('home')}>
            <div className="w-8 h-8 rounded bg-orange-600 flex items-center justify-center text-white font-serif">V</div>
            <span>LL<span className="text-orange-600">VALVE</span></span>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed mb-6">
            Global leader in manufacturing industrial valves for Oil & Gas, Chemical, and Power industries.
          </p>
          <div className="flex gap-4">
             {['in', 'fb', 'tw'].map(s => (
                <a key={s} href="#" onClick={(e) => e.preventDefault()} className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center hover:bg-orange-600 hover:text-white transition-all cursor-pointer text-xs font-bold uppercase">
                    {s}
                </a>
             ))}
          </div>
        </div>
        <div>
          <h4 className="text-white font-bold mb-6 text-sm uppercase tracking-wider">Quick Links</h4>
          <ul className="space-y-3 text-sm">
            {[
              { label: 'About Us', page: 'about' },
              { label: 'Products', page: 'products' },
              { label: 'Custom Solutions', page: 'custom' },
              { label: 'Contact', page: 'contact' }
            ].map(item => (
              <li key={item.label} onClick={() => setPage(item.page)} className="hover:text-orange-500 cursor-pointer transition-colors flex items-center gap-2">
                  <span className="w-1 h-1 bg-slate-600 rounded-full"></span> {item.label}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-6 text-sm uppercase tracking-wider">Products</h4>
          <ul className="space-y-3 text-sm">
            {['Ball Valves', 'Gate Valves', 'Butterfly Valves', 'Control Valves'].map(item => (
              <li key={item} onClick={() => setPage('products')} className="hover:text-orange-500 cursor-pointer transition-colors flex items-center gap-2">
                  <span className="w-1 h-1 bg-slate-600 rounded-full"></span> {item}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-6 text-sm uppercase tracking-wider">Certifications</h4>
          <div className="flex flex-wrap gap-3 opacity-80">
             <div className="bg-slate-900 border border-slate-800 px-3 py-2 rounded text-xs font-mono text-slate-300">ISO 9001</div>
             <div className="bg-slate-900 border border-slate-800 px-3 py-2 rounded text-xs font-mono text-slate-300">API 6D</div>
             <div className="bg-slate-900 border border-slate-800 px-3 py-2 rounded text-xs font-mono text-slate-300">CE PED</div>
          </div>
        </div>
      </div>
      <div className="border-t border-slate-900 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-slate-600">
        <p>&copy; 2025 LLValve Co., Ltd. All rights reserved.</p>
        <div className="flex flex-wrap gap-6 mt-4 md:mt-0 items-center">
          <span className="hover:text-slate-400 cursor-pointer" onClick={() => setPage('about')}>Privacy Policy</span>
          <span className="hover:text-slate-400 cursor-pointer" onClick={() => setPage('about')}>Terms</span>
          
          {/* Admin Login Link - NOW VISIBLE AND CLEAR */}
          <button 
            onClick={() => setPage('admin')} 
            className="flex items-center gap-2 px-4 py-2 rounded border border-slate-700 bg-slate-900 hover:bg-orange-600 hover:border-orange-600 hover:text-white transition-all text-xs font-bold uppercase tracking-wider ml-auto md:ml-4 group"
          >
            <Lock size={14} className="text-slate-500 group-hover:text-white transition-colors" /> 
            <span>Admin Login</span>
          </button>
        </div>
      </div>
    </div>
  </footer>
);

// --- Main App Component ---

export default function LLValveApp() {
  const [lang, setLang] = useState('en');
  const [currentPage, setCurrentPage] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  
  // Data State
  const [inquiries, setInquiries] = useState([]);
  const [news, setNews] = useState([]);
  const [imagesConfig, setImagesConfig] = useState({});

  // 1. Auth Init (With safety checks for missing config)
  useEffect(() => {
    if (!firebaseInitialized) return;

    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (e) {
        console.error("Auth Error:", e);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  // 2. Data Sync
  useEffect(() => {
    if (!user || !firebaseInitialized) return;

    // Inquiries Sync (Actually public write, but here we read for admin)
    const unsubInq = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'inquiries'), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Sort JS side: latest first
      data.sort((a, b) => b.timestamp - a.timestamp);
      setInquiries(data);
    }, (e) => console.error("Inq sync error", e));

    // News Sync
    const unsubNews = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'news'), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setNews(data);
    }, (e) => console.error("News sync error", e));

    // Images Sync
    const unsubImg = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'images'), (snapshot) => {
      const data = snapshot.docs.map(doc => doc.data());
      data.sort((a, b) => a.timestamp - b.timestamp);
      const configMap = {};
      data.forEach(item => {
        if(item.key && item.url) configMap[item.key] = item.url;
      });
      setImagesConfig(configMap);
    }, (e) => console.error("Img sync error", e));

    return () => { unsubInq(); unsubNews(); unsubImg(); };
  }, [user]);

  // Scroll Handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setMobileMenuOpen(false);
  }, [currentPage]);

  const navItems = [
    { key: 'home', label: TRANSLATIONS[lang].nav.home },
    { key: 'products', label: TRANSLATIONS[lang].nav.products },
    { key: 'custom', label: TRANSLATIONS[lang].nav.custom },
    { key: 'about', label: TRANSLATIONS[lang].nav.about },
    { key: 'contact', label: TRANSLATIONS[lang].nav.contact },
  ];

  // Render Admin Page specifically
  if (currentPage === 'admin') {
    if (!isAdminLoggedIn) {
      return <AdminLogin onLogin={setIsAdminLoggedIn} />;
    }
    return (
      <AdminDashboard 
        inquiries={inquiries} 
        news={news} 
        imagesConfig={imagesConfig}
        user={user}
        onLogout={() => { setIsAdminLoggedIn(false); setCurrentPage('home'); }} 
        firebaseReady={firebaseInitialized}
      />
    );
  }

  return (
    <div className="font-sans text-slate-800 bg-white min-h-screen flex flex-col selection:bg-orange-100 selection:text-orange-900">
      
      {/* --- Navbar --- */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg py-3' : 'bg-gradient-to-b from-black/50 to-transparent py-6'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          {/* Logo */}
          <div 
            className="text-2xl font-extrabold tracking-tighter cursor-pointer flex items-center gap-2 group"
            onClick={() => setCurrentPage('home')}
          >
            <div className={`w-9 h-9 rounded bg-orange-600 flex items-center justify-center text-white font-serif shadow-lg group-hover:scale-110 transition-transform`}>V</div>
            <span className={isScrolled ? 'text-blue-950' : 'text-white drop-shadow-md'}>
              LL<span className="text-orange-600">VALVE</span>
            </span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() => setCurrentPage(item.key)}
                className={`text-sm font-bold tracking-wide transition-colors relative group ${
                  currentPage === item.key ? 'text-orange-500' : (isScrolled ? 'text-slate-600 hover:text-orange-500' : 'text-white/90 hover:text-white')
                }`}
              >
                {item.label}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-orange-500 transition-all duration-300 ${currentPage === item.key ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="hidden lg:flex items-center gap-4">
            <button 
              onClick={() => setLang(lang === 'en' ? 'zh' : 'en')}
              className={`flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full border transition-all ${
                isScrolled ? 'border-slate-200 text-slate-600 hover:border-orange-500 hover:text-orange-500' : 'border-white/20 text-white bg-white/10 hover:bg-white/20 backdrop-blur-md'
              }`}
            >
              <Globe size={14} />
              {lang === 'en' ? 'EN' : '中'}
            </button>
            <Button variant="primary" className="py-2 px-5 text-sm shadow-none" onClick={() => setCurrentPage('contact')}>
              {lang === 'en' ? 'Get Quote' : '获取报价'}
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button className={`lg:hidden transition-colors ${isScrolled ? 'text-slate-800' : 'text-white'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: '100vh', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden fixed inset-0 top-[60px] bg-white z-40 overflow-y-auto"
            >
              <div className="flex flex-col p-8 gap-6">
                {navItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setCurrentPage(item.key)}
                    className={`text-left text-2xl font-bold border-b border-slate-100 pb-4 ${currentPage === item.key ? 'text-orange-600' : 'text-slate-800'}`}
                  >
                    {item.label}
                  </button>
                ))}
                
                <div className="flex justify-between items-center mt-4">
                  <span className="text-slate-500 text-sm font-bold uppercase tracking-wider">Language / 语言</span>
                  <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
                    <button onClick={() => setLang('en')} className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${lang === 'en' ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500'}`}>EN</button>
                    <button onClick={() => setLang('zh')} className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${lang === 'zh' ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500'}`}>中文</button>
                  </div>
                </div>

                {/* Added Admin Link to Mobile Menu */}
                <button 
                  onClick={() => { setCurrentPage('admin'); setMobileMenuOpen(false); }} 
                  className="mt-8 flex items-center gap-3 text-slate-500 hover:text-orange-600 font-bold border-t border-slate-100 pt-6"
                >
                  <Lock size={20} /> Access Admin Console
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* --- Main Content Switcher --- */}
      <main className="flex-grow">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            {currentPage === 'home' && (
              <>
                <Hero lang={lang} setPage={setCurrentPage} dynamicImage={imagesConfig['hero']} />
                <ServicesGrid lang={lang} setPage={setCurrentPage} />
                <StatsSection lang={lang} />
                <ProductShowcase lang={lang} limit={true} setPage={setCurrentPage} />
                <NewsSection lang={lang} news={news} />
                <CustomProcess lang={lang} setPage={setCurrentPage} />
                <ContactForm lang={lang} firebaseReady={firebaseInitialized} user={user} />
              </>
            )}

            {currentPage === 'products' && (
              <div className="pt-20">
                <div className="bg-blue-950 text-white py-20 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 relative z-10">{TRANSLATIONS[lang].nav.products}</h1>
                  <p className="text-blue-200 text-lg relative z-10">Complete Industrial Valve Catalog</p>
                </div>
                <ProductShowcase lang={lang} limit={false} setPage={setCurrentPage} />
                <StatsSection lang={lang} />
              </div>
            )}

            {currentPage === 'custom' && (
              <div className="pt-20">
                <div className="bg-blue-950 text-white py-20 text-center relative overflow-hidden">
                   <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 relative z-10">{TRANSLATIONS[lang].nav.custom}</h1>
                  <p className="text-blue-200 text-lg relative z-10">Tailored Engineering for Extreme Conditions</p>
                </div>
                <CustomProcess lang={lang} setPage={setCurrentPage} />
                <ContactForm lang={lang} firebaseReady={firebaseInitialized} user={user} />
              </div>
            )}

             {currentPage === 'about' && (
              <div className="pt-20">
                 <div className="bg-blue-950 text-white py-20 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 relative z-10">{TRANSLATIONS[lang].nav.about}</h1>
                  <p className="text-blue-200 text-lg relative z-10">Excellence Since 2004</p>
                </div>
                <div className="container mx-auto px-6 py-20 max-w-5xl text-center">
                    <h2 className="text-3xl font-bold text-blue-950 mb-8">Engineering the Future of Flow Control</h2>
                    <p className="text-lg text-slate-600 leading-relaxed mb-16 max-w-3xl mx-auto">
                        To provide the most reliable fluid control solutions for the world's critical infrastructure. 
                        LLValve combines state-of-the-art manufacturing with rigorous quality control to ensure safety and efficiency in every unit we ship.
                    </p>
                    <div className="rounded-2xl overflow-hidden shadow-2xl mb-16 border-4 border-white">
                      <SafeImage src={imagesConfig['about'] || DEFAULT_IMAGES.about} className="w-full h-[400px] object-cover" alt="Factory Team" />
                    </div>
                    <StatsSection lang={lang} />
                </div>
              </div>
            )}

            {currentPage === 'contact' && (
              <div className="pt-20">
                 <div className="bg-blue-950 text-white py-20 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 relative z-10">{TRANSLATIONS[lang].nav.contact}</h1>
                  <p className="text-blue-200 text-lg relative z-10">Professional Support within 24 Hours</p>
                </div>
                <ContactForm lang={lang} firebaseReady={firebaseInitialized} user={user} />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* --- Floating Action --- */}
      <motion.div 
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1 }}
        className="fixed bottom-8 right-8 z-40"
      >
        <button 
          onClick={() => setCurrentPage('contact')}
          className="bg-green-500 hover:bg-green-600 text-white w-14 h-14 rounded-full shadow-lg shadow-green-500/30 transition-transform hover:scale-110 flex items-center justify-center"
        >
          <MessageSquare size={26} />
        </button>
      </motion.div>

      <Footer lang={lang} setPage={setCurrentPage} />
    </div>
  );
}
